import React from "react";
import { useT } from "../i18n/I18nProvider";

const DAY_KEYS = ["day_mon", "day_tue", "day_wed", "day_thu", "day_fri", "day_sat", "day_sun"];

function todayIndex() {
  const js = new Date().getDay(); // 0=Sun
  return js === 0 ? 6 : js - 1;
}

export default function Visit() {
  const { t } = useT();
  const today = todayIndex();
  const tWeek = t("visit.t_weekday");
  const tSun = t("visit.t_sun");
  const hours = DAY_KEYS.map((k, i) => ({
    day: t(`visit.${k}`),
    time: i === 6 ? tSun : tWeek,
  }));

  return (
    <section id="visit" data-testid="visit-section" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12">
        <div className="lg:col-span-5">
          <div className="text-[12px] uppercase tracking-[0.22em] text-[#b8420f] font-bold mb-3">
            {t("visit.eyebrow")}
          </div>
          <h2 className="font-display font-black text-stone-900 text-[38px] md:text-[48px] leading-[1.05] tracking-tight mb-6">
            {t("visit.h_a")}
            <br />
            <span className="italic text-[#b8420f]">{t("visit.h_b")}</span>
          </h2>

          <div className="space-y-5 text-[16px]">
            <a
              href="https://www.google.com/maps/search/?api=1&query=5609+E+Watauga+Rd+Watauga+TX+76148"
              target="_blank"
              rel="noreferrer"
              data-testid="visit-address-link"
              className="flex items-start gap-4 group"
            >
              <span className="mt-0.5 w-10 h-10 rounded-full bg-[#fbeadd] flex items-center justify-center text-[#b8420f] flex-shrink-0 group-hover:bg-[#b8420f] group-hover:text-white transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </span>
              <div>
                <div className="font-bold text-stone-900">{t("visit.address_label")}</div>
                <div className="text-stone-600 group-hover:text-[#b8420f] transition-colors">{t("visit.address_value")}</div>
              </div>
            </a>

            <a href="tel:+18176561046" data-testid="visit-phone-link" className="flex items-start gap-4 group">
              <span className="mt-0.5 w-10 h-10 rounded-full bg-[#e8efe9] flex items-center justify-center text-[#14532d] flex-shrink-0 group-hover:bg-[#14532d] group-hover:text-white transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.93.37 1.84.7 2.71a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.37-1.27a2 2 0 0 1 2.11-.45c.87.33 1.78.57 2.71.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              <div>
                <div className="font-bold text-stone-900">{t("visit.phone_label")}</div>
                <div className="text-stone-600 group-hover:text-[#14532d] transition-colors">{t("visit.phone_value")}</div>
              </div>
            </a>

            <a href="mailto:elsolraul@yahoo.com" data-testid="visit-email-link" className="flex items-start gap-4 group">
              <span className="mt-0.5 w-10 h-10 rounded-full bg-[#e8efe9] flex items-center justify-center text-[#14532d] flex-shrink-0 group-hover:bg-[#14532d] group-hover:text-white transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
              </span>
              <div>
                <div className="font-bold text-stone-900">{t("visit.email_label")}</div>
                <div className="text-stone-600 group-hover:text-[#14532d] transition-colors">{t("visit.email_value")}</div>
              </div>
            </a>
          </div>

          <div className="mt-9 bg-[#f5efde] border border-stone-300/60 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-xl text-stone-900">{t("visit.hours_title")}</div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#14532d] bg-[#e8efe9] px-2.5 py-1 rounded-full uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#14532d] animate-pulse" />
                {t("visit.open_today")}
              </span>
            </div>
            <ul className="divide-y divide-stone-300/60" data-testid="hours-list">
              {hours.map((h, i) => (
                <li
                  key={i}
                  className={`flex items-center justify-between py-2.5 text-[15px] ${
                    i === today ? "font-bold text-[#b8420f]" : "text-stone-700"
                  }`}
                >
                  <span>{h.day}{i === today && t("visit.today_suffix")}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-[28px] overflow-hidden shadow-[0_30px_70px_-25px_rgba(28,25,23,0.35)] border border-stone-200 bg-white aspect-[4/3] lg:aspect-auto lg:h-full min-h-[420px]">
            <iframe
              data-testid="visit-map"
              title={t("visit.map_title")}
              src="https://www.google.com/maps?q=5609%20E%20Watauga%20Rd%2C%20Watauga%2C%20TX%2076148&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 420 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
}
